/**
 * Hooks TanStack Query per FASE 3 — Mercato (operazioni + state machine).
 *
 * Bridge v1.4 Sez. 1.12 + App. D:
 *   8 tipi: acquisto, vendita, scambio, scambio_conguaglio,
 *           prestito_secco, prestito_ddr, prestito_odr, prestito_odr_condizionato
 *   Stati: proposta → {accettata, rifiutata, annullata, controproposta_emessa,
 *                       auto_rifiutata_budget, auto_rifiutata_giocatore}
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuthStore } from "../lib/auth-store";

// === Enum mirror dei modelli backend ===
export type TipoOperazione =
  | "acquisto"
  | "vendita"
  | "scambio"
  | "scambio_conguaglio"
  | "prestito_secco"
  | "prestito_ddr"
  | "prestito_odr"
  | "prestito_odr_condizionato";

export type StatoOperazione =
  | "proposta"
  | "controproposta_emessa"
  | "accettata"
  | "rifiutata"
  | "annullata"
  | "auto_rifiutata_budget"
  | "auto_rifiutata_giocatore";

export type DirezioneGiocatore = "entra" | "esce";

export interface GiocatoreOpItem {
  giocatore_id: string;
  direzione: DirezioneGiocatore;
  /** Nome giocatore (Andrea 2026-05-19: resoconti leggibili). */
  nome?: string | null;
  club_serie_a?: string | null;
}

export interface OperazioneItem {
  id: string;
  tipo: TipoOperazione;
  stato: StatoOperazione;
  squadra_proponente_id: string;
  squadra_destinataria_id: string;
  /** Nomi squadre (Andrea 2026-05-19): pre-joined dal backend per
   * evitare lookup lato client. */
  nome_squadra_proponente?: string | null;
  nome_squadra_destinataria?: string | null;
  finestra_mercato_id: string;
  operazione_padre_id: string | null;
  importo_offerto_milioni: string | null;
  conguaglio_milioni: string | null;
  importo_riscatto_milioni: string | null;
  condizione_riscatto_testo: string | null;
  messaggio_proponente: string | null;
  data_proposta: string;
  data_risposta: string | null;
  data_scadenza: string | null;
  giocatori: GiocatoreOpItem[];
}

export interface ThreadResponse {
  root: OperazioneItem;
  catena: OperazioneItem[];
}

// === Payloads ===
export interface CreaPropostaPayload {
  finestra_id: string;
  tipo: TipoOperazione;
  squadra_proponente_id: string;
  squadra_destinataria_id: string;
  giocatori: GiocatoreOpItem[];
  importo_offerto_milioni?: string | null;
  conguaglio_milioni?: string | null;
  importo_riscatto_milioni?: string | null;
  condizione_riscatto_testo?: string | null;
  messaggio?: string | null;
}

export interface ContropponiPayload {
  tipo: TipoOperazione;
  giocatori: GiocatoreOpItem[];
  importo_offerto_milioni?: string | null;
  conguaglio_milioni?: string | null;
  importo_riscatto_milioni?: string | null;
  condizione_riscatto_testo?: string | null;
  messaggio?: string | null;
}

// === Queries ===

export interface UseListaOperazioniArgs {
  squadraId?: string | null;
  stato?: StatoOperazione | null;
  limit?: number;
  enabled?: boolean;
}

export function useListaOperazioni({
  squadraId,
  stato,
  limit = 100,
  enabled = true,
}: UseListaOperazioniArgs) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["mercato", "lista", squadraId ?? null, stato ?? null, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (squadraId) params.set("squadra_id", squadraId);
      if (stato) params.set("stato", stato);
      params.set("limit", String(limit));
      const res = await api.get<OperazioneItem[]>(
        `/mercato/operazioni?${params.toString()}`,
      );
      return res.data;
    },
    enabled: isAuth && enabled,
  });
}

export function useOperazione(opId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["mercato", "operazione", opId],
    queryFn: async () => {
      const res = await api.get<OperazioneItem>(`/mercato/operazioni/${opId}`);
      return res.data;
    },
    enabled: isAuth && !!opId,
  });
}

export function useThreadOperazione(opId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["mercato", "thread", opId],
    queryFn: async () => {
      const res = await api.get<ThreadResponse>(
        `/mercato/operazioni/${opId}/thread`,
      );
      return res.data;
    },
    enabled: isAuth && !!opId,
  });
}

// === Mutations ===

export function useCreaProposta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreaPropostaPayload) => {
      const res = await api.post<OperazioneItem>(
        "/mercato/operazioni",
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mercato"] });
    },
  });
}

export function useAccettaOperazione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      opId,
      stagioneId,
    }: {
      opId: string;
      stagioneId: string;
    }) => {
      const res = await api.post<OperazioneItem>(
        `/mercato/operazioni/${opId}/accetta`,
        { stagione_id: stagioneId },
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mercato"] });
      qc.invalidateQueries({ queryKey: ["budget"] });
      qc.invalidateQueries({ queryKey: ["rosa"] });
    },
  });
}

export function useRifiutaOperazione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (opId: string) => {
      const res = await api.post<OperazioneItem>(
        `/mercato/operazioni/${opId}/rifiuta`,
      );
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mercato"] }),
  });
}

export function useAnnullaOperazione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (opId: string) => {
      const res = await api.post<OperazioneItem>(
        `/mercato/operazioni/${opId}/annulla`,
      );
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mercato"] }),
  });
}

export function useContropponi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      opId,
      payload,
    }: {
      opId: string;
      payload: ContropponiPayload;
    }) => {
      const res = await api.post<OperazioneItem>(
        `/mercato/operazioni/${opId}/contropponi`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mercato"] }),
  });
}

// === Metadati 8 tipi (UI helpers) ===

export interface TipoOperazioneMeta {
  tipo: TipoOperazione;
  label: string;
  emoji: string;
  descrizione: string;
  /** Quale lato manda il giocatore principale */
  direzionePrincipale: DirezioneGiocatore;
  /** Permette anche giocatore in direzione opposta (scambi) */
  ammetteScambio: boolean;
  /** Campi importo richiesti */
  campi: {
    importoOfferto: "required" | "optional" | "hidden";
    conguaglio: "required" | "optional" | "hidden";
    importoRiscatto: "required" | "optional" | "hidden";
    condizioneRiscatto: "required" | "optional" | "hidden";
  };
}

export const TIPI_OPERAZIONE: TipoOperazioneMeta[] = [
  {
    tipo: "acquisto",
    label: "Acquisto",
    emoji: "💰",
    descrizione:
      "Offerta in denaro per un giocatore del destinatario. Nessun rinnovo trasferito.",
    direzionePrincipale: "entra",
    ammetteScambio: false,
    campi: {
      importoOfferto: "required",
      conguaglio: "hidden",
      importoRiscatto: "hidden",
      condizioneRiscatto: "hidden",
    },
  },
  {
    tipo: "vendita",
    label: "Vendita",
    emoji: "📤",
    descrizione:
      "Cessione di un tuo giocatore al destinatario. Post-mercato: niente rimborso rinnovo.",
    direzionePrincipale: "esce",
    ammetteScambio: false,
    campi: {
      importoOfferto: "required",
      conguaglio: "hidden",
      importoRiscatto: "hidden",
      condizioneRiscatto: "hidden",
    },
  },
  {
    tipo: "scambio",
    label: "Scambio",
    emoji: "🔁",
    descrizione: "Scambio puro di 1+ giocatori, senza importi.",
    direzionePrincipale: "esce",
    ammetteScambio: true,
    campi: {
      importoOfferto: "hidden",
      conguaglio: "hidden",
      importoRiscatto: "hidden",
      condizioneRiscatto: "hidden",
    },
  },
  {
    tipo: "scambio_conguaglio",
    label: "Scambio + conguaglio",
    emoji: "⚖️",
    descrizione:
      "Scambio con conguaglio in fantamilioni dal proponente (positivo = paghi).",
    direzionePrincipale: "esce",
    ammetteScambio: true,
    campi: {
      importoOfferto: "hidden",
      conguaglio: "required",
      importoRiscatto: "hidden",
      condizioneRiscatto: "hidden",
    },
  },
  {
    tipo: "prestito_secco",
    label: "Prestito secco",
    emoji: "📥",
    descrizione:
      "Prestito fino a fine stagione, ritorna al proprietario originale.",
    direzionePrincipale: "entra",
    ammetteScambio: false,
    campi: {
      importoOfferto: "optional",
      conguaglio: "hidden",
      importoRiscatto: "hidden",
      condizioneRiscatto: "hidden",
    },
  },
  {
    tipo: "prestito_ddr",
    label: "Prestito + DDR",
    emoji: "🔓",
    descrizione: "Prestito con diritto di riscatto a cifra concordata.",
    direzionePrincipale: "entra",
    ammetteScambio: false,
    campi: {
      importoOfferto: "optional",
      conguaglio: "hidden",
      importoRiscatto: "required",
      condizioneRiscatto: "optional",
    },
  },
  {
    tipo: "prestito_odr",
    label: "Prestito + ODR",
    emoji: "🔒",
    descrizione: "Prestito con obbligo di riscatto a fine stagione.",
    direzionePrincipale: "entra",
    ammetteScambio: false,
    campi: {
      importoOfferto: "optional",
      conguaglio: "hidden",
      importoRiscatto: "required",
      condizioneRiscatto: "hidden",
    },
  },
  {
    tipo: "prestito_odr_condizionato",
    label: "Prestito + ODR condizionato",
    emoji: "❓",
    descrizione:
      "Riscatto obbligatorio al verificarsi di una condizione oggettiva (verifica admin).",
    direzionePrincipale: "entra",
    ammetteScambio: false,
    campi: {
      importoOfferto: "optional",
      conguaglio: "hidden",
      importoRiscatto: "required",
      condizioneRiscatto: "required",
    },
  },
];

export function metaPerTipo(t: TipoOperazione): TipoOperazioneMeta {
  const m = TIPI_OPERAZIONE.find((x) => x.tipo === t);
  if (!m) throw new Error(`Tipo operazione sconosciuto: ${t}`);
  return m;
}
